import * as React from 'react';
import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { LinkValue, PortableValue } from '@dealstack/shared/lib/frontend/common-ui/blog-types.ts';
import { generatePath, Link } from 'react-router-dom';
import { Title } from '@mantine/core';
import { SanityRichTextContainer } from '@dealstack/frontend/app/pages/HelpBlog/SanityRichText/SanityRichText.styles.tsx';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	makeDynamicHelpBlogUrl,
	prefixHelpBlogNodeKey,
} from '@dealstack/frontend/app/pages/HelpBlog/help-blog.utils.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Callout } from '@dealstack/frontend/shared/components/Callout/Callout.tsx';
import { ReactNode, useMemo } from 'react';
import { SanityImageSource, ImageUrlBuilder } from '@sanity/image-url';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';

interface Props {
	value: PortableValue;
	mediaResolver: (source: SanityImageSource) => ImageUrlBuilder;
	fontSize?: string;
	hideMedia?: boolean;
	dealId?: string;
}

export const SanityRichText = ({ value, mediaResolver, hideMedia, fontSize, dealId }: Props) => {
	const components: PortableTextComponents = useMemo(
		() => ({
			types: {
				image: ({ value }) =>
					hideMedia ? null : (
						<img
							src={mediaResolver(value).auto('format').url()}
							width="100%"
							alt={value?.alt || ''}
							loading="lazy"
						/>
					),
				video: ({ value }) => {
					const videoUrl = value?.url;

					if (!videoUrl) {
						return null;
					}

					const videoId = videoUrl?.split('v=')[1]?.split('&')[0];
					const youtubeEmbedUrl = `https://www.youtube.com/embed/${videoId}`;

					return hideMedia ? null : (
						<div className="video">
							<iframe
								title={value?.title || 'video'}
								src={youtubeEmbedUrl}
								loading="lazy"
								style={{ aspectRatio: '16 / 9', border: 0 }}
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							/>
						</div>
					);
				},
				quote: ({ value }) => {
					return (
						<blockquote className="quote">
							<Title size="h4" fw={400} className="quote-text">
								{value?.text}
							</Title>
							<cite>— {value?.author}</cite>
						</blockquote>
					);
				},
				guideflow: ({ value }) => {
					const embedId = value?.embedId;

					if (!embedId) {
						return null;
					}

					const guideflowEmbedUrl = `https://app.guideflow.com/embed/${embedId}`;
					const scriptUrl = 'https://app.guideflow.com/assets';

					return hideMedia ? null : (
						<div className="guideflow">
							<iframe
								id={embedId}
								title={value?.title || 'guideflow'}
								src={guideflowEmbedUrl}
								width="100%"
								height="100%"
								style={{ overflow: 'hidden', aspectRatio: '16 / 9', border: 0 }}
								scrolling="no"
								allow="clipboard-read; clipboard-write"
								allowFullScreen
							/>

							<script src={scriptUrl} data-iframe-id={embedId} />
						</div>
					);
				},
				lineBreak: ({ value }) => {
					const verticalMargin = `${value?.spacing || 0}rem`;
					return (
						<hr
							style={{
								height: '1px',
								margin: `${verticalMargin} 0`,
								border: 0,
								background: `${defaultTheme.v3.colors.gray[4]}`,
							}}
						></hr>
					);
				},
				callout: ({ value }) => {
					const icon = value?.icon;
					const iconImage = icon ? mediaResolver(icon).auto('format').url() : undefined;
					const title = value?.title;
					const body = value?.body;
					const button = value?.button;

					const linkComponent = button?.text
						? makeLinkComponent(button, dealId, button?.text)
						: undefined;

					if (!title) {
						return null;
					}

					return (
						<Callout
							imageUrl={iconImage}
							imageAlt={title}
							title={title}
							body={body}
							LinkComponent={linkComponent}
						/>
					);
				},
			},
			block: {
				h1: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={1}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				h2: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={2}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				h3: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={3}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				h4: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={4}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				h5: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={5}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				h6: ({ children, value }) => (
					<Title
						id={value._key ? prefixHelpBlogNodeKey(value._key) : undefined}
						order={6}
						fw={600}
						className="title"
					>
						{children}
					</Title>
				),
				normal: ({ children }) => <p>{children}</p>,
				blockquote: ({ children }) => <blockquote>{children}</blockquote>,
			},
			marks: {
				strong: ({ children }) => <strong>{children}</strong>,
				em: ({ children }) => <em>{children}</em>,
				underline: ({ children }) => <u>{children}</u>,
				code: ({ children }) => <code>{children}</code>,
				link: ({ value, children }) => {
					return makeLinkComponent(value, dealId, children);
				},
			},
			list: {
				bullet: ({ children }) => <ul>{children}</ul>,
				number: ({ children }) => <ol>{children}</ol>,
			},
			listItem: {
				bullet: ({ children }) => <li>{children}</li>,
				number: ({ children }) => <li>{children}</li>,
			},
		}),
		[mediaResolver, hideMedia, dealId]
	);

	if (!value || value.length === 0) {
		return null;
	}

	return (
		<SanityRichTextContainer fontSize={fontSize}>
			<PortableText value={value} components={components} />
		</SanityRichTextContainer>
	);
};

const makeLinkComponent = (
	linkValue: LinkValue | undefined,
	dealId: string | undefined,
	content: ReactNode | string
) => {
	const linkType = linkValue?.linkType;
	const openInNewTab = !!linkValue?.openInNewTab;

	switch (linkType) {
		case 'href': {
			const href = linkValue?.href || '#';

			return (
				<a
					href={href}
					target={openInNewTab ? '_blank' : undefined}
					rel={openInNewTab ? 'noopener noreferrer' : undefined}
					className="link"
				>
					{content}
				</a>
			);
		}
		case 'link': {
			const link = linkValue?.link || '#';

			return (
				<Link
					to={link}
					className="link"
					target={openInNewTab ? '_blank' : undefined}
					rel={openInNewTab ? 'noopener noreferrer' : undefined}
				>
					{content}
				</Link>
			);
		}
		case 'documentRef': {
			const resolvedSlug = linkValue?.resolved?.slug || '';
			const docType = linkValue?.resolved?.docType;
			let parentRoute = '';

			switch (docType) {
				case 'author':
					parentRoute = HELP_BLOG_ROUTES.AUTHOR;
					break;
				case 'category':
					parentRoute = HELP_BLOG_ROUTES.CATEGORY;
					break;
				default:
					parentRoute = HELP_BLOG_ROUTES.ARTICLE;
					break;
			}

			const generatedPath = generatePath(parentRoute, {
				slug: resolvedSlug,
			});
			const fullPath = makeDynamicHelpBlogUrl(dealId, `${ROUTES.HELP_BLOG}/${generatedPath}`);

			return (
				<Link
					to={fullPath}
					className="link"
					target={openInNewTab ? '_blank' : undefined}
					rel={openInNewTab ? 'noopener noreferrer' : undefined}
				>
					{content}
				</Link>
			);
		}
		default:
			return <a href="#">{content}</a>;
	}
};
